import React, { useEffect, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { Button, IconButton, TextField } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { styled } from "@mui/material/styles";
import { TimePicker } from "orion-components/CBComponents";
import isNil from "lodash/isNil";
import { DatePicker } from "orion-components/CBComponents";
import ReplyIcon from "@mui/icons-material/Reply";
import moment from "moment";
import { Translate } from "orion-components/i18n";
import { generateDirStyles } from "orion-components/SharedComponents/GenerateDirStyles/GenerateDirStyles";
import DateInput from "./components/DateInput";
import TimeInput from "./components/TimeInput";
import { updateComponentInstance } from "../../../../../SharedComponents/ComponentState/redux/actions";

const COMPONENT_KEY = "cbComponents.newVideoPlayer.videoPlayerContainer";

const CustomizedTextField = styled(TextField)(({ customStyles }) => ({
	"& label": {
		color: "#9A9FA3",
		fontSize: "1em",
		...customStyles.label
	},
	"& label.Mui-focused": {
		color: "#9A9FA3",
		...customStyles.labelFocused
	},
	"& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall": {
		padding: "0px !important"
	},
	"& .MuiList-root MuiList-padding MuiMenu-list": {
		background: "black"
	},
	"& .MuiOutlinedInput-root": {
		"& fieldset": {
			borderColor: "#5E6571",
			width: 1,
			...customStyles.fieldset
		},
		"&:hover fieldset": {
			borderColor: "#4DB5F4",
			...customStyles.fieldsetHover
		},
		"&.Mui-focused fieldset": {
			borderColor: "#4DB5F4",
			...customStyles.fieldsetFocused
		},
		...customStyles.inputRoot
	}
}));

//Using a custom dropdown because the MUI menu has issues with the popover. When the menu item is expanded, the list is generated, but the jump-to-different-time popup closes
const CustomDropdown = ({ options, value, onChange, dir }) => {
	const handleChange = event => {
		onChange(event.target.value);
	};

	return (
		<select
			value={value}
			onChange={handleChange}
			className="jumpToDifferentTime-custom-select"
			style={{ ...(dir === "ltr" && { paddingLeft: 5 }), ...(dir === "rtl" && { paddingRight: 5 }) }}
		>
			{options.map(option => (
				<option key={option.value} value={option.value}>
					{option.label}
				</option>
			))}
		</select>
	);
};

const baseStyles = {
	parentContainer: {
		display: "flex",
		zIndex: 1903
	},
	container: (displayModal, position) => ({
		minWidth: 250,
		display: displayModal !== null ? "flex" : "none", // Change to flex for flex behavior
		flexDirection: "row", // Allow horizontal growth
		flexGrow: 1, // Enables the modal to grow if the parent allows
		position: "absolute",
		bottom: "32.5%",
		...(position === "right" && { right: "0.5%" }),
		color: "#fff",
		zIndex: 2000
	}),
	modalContent: {
		borderRadius: "5px",
		padding: "5px 7.5px",
		flexGrow: 1,
		display: "flex",
		flexDirection: "column",
		minHeight: 105,
		overflowY: "scroll",
		background: "rgba(29, 29, 31, 0.5) 0% 0% no-repeat padding-box",
		boxShadow: "0px 0px 6px #0000009A",
		opacity: 1,
		backdropFilter: "blur(10px)",
		WebkitBackdropFilter: "blur(10px)",
		cursor: "pointer",
		width: "100%"
	},
	header: { display: "flex", height: 20, alignItems: "center", fontSize: 12 },
	chevronIcon: { width: 24.5, height: 24.5, marginLeft: "-6px", marginRight: "8px" },
	divider: {
		left: "-7.5px",
		width: "110%",
		height: 0.5,
		backgroundColor: "#FFFFFF",
		opacity: 0.35,
		top: 28,
		position: "absolute",
		display: "flex",
		flexGrow: 1,
		flexDirection: "row"
	},
	dateTimeContainer: {
		display: "flex",
		alignItems: "center",
		marginTop: 25,
		marginBottom: 10,
		fontSize: "1em"
	},
	timeText: { marginRight: 5 },
	timeContainer: { marginLeft: 10 },
	durationContainer: { height: 25, display: "flex", alignItems: "center", marginTop: 10 },
	durationText: { marginRight: 5 },
	durationControl: { marginRight: 5, marginTop: 5, display: "inline-flex", fontSize: 12 },
	actionButtons: {
		float: "right",
		marginTop: "5%",
		marginRight: "10%",
		display: "flex",
		justifyContent: "center",
		fontSize: 8
	}
};

const getParsedDateTime = (date, time) => {
	const timeMoment = moment(time);
	// Parse the date string to get a moment object for the date
	const parsedDate = moment(new Date(date));

	// Set the time of parsedDate to the time from timeMoment
	parsedDate.hours(timeMoment.hours());
	parsedDate.minutes(timeMoment.minutes());
	parsedDate.seconds(timeMoment.seconds());
	parsedDate.milliseconds(timeMoment.milliseconds());
	return parsedDate;
};

const JumpToDifferentTime = React.forwardRef((props, ref) => {
	const {
		open,
		closePopup,
		position,
		instanceId,
		isDateTimeFor = "endDate",
		setStartDateExternally,
		setVideoDurationExternally
	} = props;
	const [displayModal, setDisplayModal] = useState(null);
	const dir = useSelector(state => getDir(state));
	const [duration, setDuration] = useState(30);
	const styles = generateDirStyles(baseStyles, dir);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [showTimePicker, setShowTimePicker] = useState(false);
	const [error, setError] = useState("");
	const [time, setTime] = useState(new Date());
	const [date, setDate] = useState(new Date().getTime());
	const dispatch = useDispatch();
	const containerStyle = generateDirStyles(
		{ container: baseStyles.container(displayModal, position) },
		dir
	).container;
	const timeFormatPreference = useSelector(state => state.appState.global.timeFormat);

	const handlePopoverClose = () => {
		setDisplayModal(null);
		closePopup();
		dispatch(
			updateComponentInstance(COMPONENT_KEY, instanceId, {
				components: { recorded: { jumpToDifferentTime: { isOpen: false } } }
			})
		);
	};

	useEffect(() => {
		if (open) {
			setDisplayModal(true);
			dispatch(
				updateComponentInstance(COMPONENT_KEY, instanceId, {
					components: { recorded: { jumpToDifferentTime: { isOpen: true } } }
				})
			);
		}
	}, [open]);

	useEffect(() => {
		return () => {
			setDisplayModal(null);
			closePopup();
		};
	}, []);

	const handleChange = name => event => {
		const value = event && event.target && !isNil(event.target.value) ? event.target.value : event;
		if (name === "time") {
			setTime(value);
		}
		if (name === "date") {
			setDate(value);
		}
	};

	const options = [
		{ value: "seconds", label: "Seconds" },
		{ value: "minutes", label: "Minutes" },
		{ value: "hours", label: "Hours" }
	];

	const [selectedValue, setSelectedValue] = useState("minutes");

	const handleChangeDropDown = value => {
		setSelectedValue(value);
	};

	const handleSubmit = () => {
		// Normalize duration to minutes for the video duration payload
		let finalDuration = duration;
		if (selectedValue === "seconds") finalDuration = finalDuration / 60;
		if (selectedValue === "hours") finalDuration = finalDuration * 60;

		const parsed = getParsedDateTime(date, time); // moment-like
		let startDateTimeVal = null;
		let endDateTimeVal = null;

		if (isDateTimeFor === "startDate") {
			startDateTimeVal = parsed.toString();
			endDateTimeVal = duration ? parsed.clone().add(duration, selectedValue).toString() : null;

			setStartDateExternally({
				instanceId,
				startDateTimeVal,
				endDateTimeVal,
				isFor: "startDate"
			});
		} else {
			endDateTimeVal = parsed.toString();
			startDateTimeVal = duration ? parsed.clone().subtract(duration, selectedValue).toString() : null;

			setStartDateExternally({
				instanceId,
				startDateTimeVal,
				endDateTimeVal,
				isFor: "endDate"
			});
		}
		setVideoDurationExternally({ instanceId, duration: finalDuration });
		handlePopoverClose();
	};

	const getValuesFromChildren = (type, value) => {
		switch (type) {
			case "date":
				setDate(value);
				global.JumpToDifferentTime_date = value;
				break;
			case "time":
				setTime(value);
				global.JumpToDifferentTime_time = value;
				break;
			case "error":
				setError(value);
				break;
			default:
				console.warn(`Unhandled type: ${type}`);
		}
	};

	const getTime = val => {
		setTime(val);
	};
	useImperativeHandle(ref, () => ({
		getTime
	}));

	return (
		<div style={styles?.parentContainer}>
			<div style={containerStyle}>
				<div style={styles.modalContent}>
					<div style={styles.header}>
						<IconButton
							onClick={() => {
								handlePopoverClose();
							}}
							sx={{ color: "#FFFFFF", padding: "0px !important" }}
						>
							{dir === "ltr" ? (
								<ChevronLeftIcon sx={styles.chevronIcon} />
							) : (
								<ChevronRightIcon sx={styles.chevronIcon} />
							)}
						</IconButton>
						<div style={{ fontSize: "1em" }}>
							<Translate value="global.CBComponents.newVideoPlayer.components.recorded.jumpToDifferentTime.header" />
						</div>
					</div>
					<div style={styles.divider}></div>
					<div style={{ fontSize: 10 }}>
						<div style={styles.dateTimeContainer}>
							<div style={styles.timeText}>
								<Translate value="global.CBComponents.newVideoPlayer.components.recorded.jumpToDifferentTime.time" />
							</div>
							<div style={{ display: "flex" }}>
								<DateInput
									setShowDatePicker={setShowDatePicker}
									datePickerDate={date}
									sendValuesToParent={getValuesFromChildren}
									error={error}
								/>
							</div>
							<div style={styles.timeContainer}>
								<TimeInput
									timePickerTime={time}
									sendValuesToParent={getValuesFromChildren}
									setShowTimePicker={setShowTimePicker}
									error={error}
								/>
							</div>
						</div>
						<div style={styles.durationContainer}>
							<div style={styles.durationText}>
								<Translate value="global.CBComponents.newVideoPlayer.components.recorded.jumpToDifferentTime.duration" />
							</div>
							<div style={styles.durationControl}>
								<CustomizedTextField
									variant="outlined"
									name="duration"
									fullWidth
									value={duration}
									onChange={e => {
										setDuration(e.target.value);
									}}
									size="small"
									customStyles={{
										fieldset: { height: 30.5, width: 40.5 },
										inputRoot: { height: 30.5, width: 40.5, padding: "0px 2px 2px 2px !important" }
									}}
									inputProps={{
										autoComplete: "off",
										style: {
											color: "#FFFFFF",
											fontSize: "0.833em",
											fontFamily: "Roboto",
											textAlign: "center"
										}
									}}
								/>
							</div>
							<div>
								<CustomDropdown
									options={options}
									value={selectedValue}
									onChange={handleChangeDropDown}
									dir={dir}
								/>
							</div>
						</div>
						<div style={{ marginTop: "5px" }}>
							{error && <p style={{ color: "red", fontSize: "10px" }}>{error}</p>}
						</div>
						<div style={styles.actionButtons}>
							<Button
								variant="text"
								style={{ fontSize: "1em", color: "#8C8C8D" }}
								onClick={() => handlePopoverClose()}
							>
								<Translate value="global.CBComponents.newVideoPlayer.components.recorded.jumpToDifferentTime.cancel" />
							</Button>

							<IconButton
								style={{ transform: "scaleX(-1)" }}
								sx={{
									width: 15,
									height: 15,
									color: "#fff",
									"&.Mui-disabled": {
										color: "#696969"
									}
								}}
								onClick={handleSubmit}
								disabled={error !== ""}
							>
								<ReplyIcon />
							</IconButton>
						</div>
					</div>
				</div>
			</div>
			<DatePicker
				id="date"
				key="date"
				externalDate={date}
				label=""
				value={date}
				handleChange={handleChange("date")}
				dir={dir}
				customDialogProps={{ zIndex: 99999999 }}
				useInputControl={false}
				showDialog={showDatePicker}
				onSubmit={value => {
					if (value === null) {
						setDate(null);
					}

					setShowDatePicker(false);
				}}
				onExternalReset={() => {
					setShowDatePicker(false);
				}}
			/>
			<TimePicker
				id="time"
				key="time"
				name="time"
				label=""
				value={time}
				handleChange={handleChange("time")}
				format={`time_${timeFormatPreference}`}
				dir={dir}
				useInputControl={false}
				showDialog={showTimePicker}
				onSubmit={value => {
					if (value === null) {
						setTime(null);
					}
					setShowTimePicker(false);
				}}
				displaySeconds={true}
				onExternalReset={value => {
					setTime(value);
					setShowTimePicker(false);
				}}
			/>
		</div>
	);
});

const areEqual = (prevProps, nextProps) => {
	if (prevProps.open !== nextProps.open) return false;

	if (prevProps.position !== nextProps.position) return false;

	if (prevProps.instanceId !== nextProps.instanceId) return false;

	return true;
};

export default React.memo(JumpToDifferentTime, areEqual);
